/**
 * `CustomArtGenerateModal` — TH-55363.
 *
 * Modal "Gerar via IA" da tela /custom-arts. Permite ao operador
 * criar uma arte sem subir PNG/JPG — escolhe categoria + texto livre
 * (opcional) + produto associado (opcional), backend gera via
 * ImageGenerator (ADR-012) sob circuit breaker per-tenant (ADR-019).
 *
 * Fluxo:
 *   1. Usuário preenche o form e clica "Gerar".
 *   2. POST /custom-arts/generate → 202 com a row em
 *      generation_status='pending'.
 *   3. Modal entra em modo "polling": exibe loader e faz GET
 *      /custom-arts/:id a cada 2s (via useCustomArtStatus).
 *   4. Quando generation_status='ready', exibe preview do art_url
 *      + ação "Concluído" (fecha e invalida a listagem).
 *   5. Se 'failed', exibe ai_error_message e botão "Tentar novamente"
 *      (refaz com mesmos parâmetros).
 *
 * Mantemos o componente separado do upload pra não inchar
 * `CustomArtUploadModal` — quem invoca o botão "Nova arte" na página
 * escolhe qual modal abrir.
 */

import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Sparkles, X as XIcon } from "lucide-react";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";
import {
  CATEGORY_LABELS,
  useCustomArtStatus,
  useGenerateCustomArt,
  type CustomArtCategory,
  type GenerateCustomArtInput,
} from "@/hooks/useGenerateCustomArt";

export interface StoreOption {
  id: string;
  name: string;
}

export interface AreaOption {
  id: string;
  name: string;
}

export interface CustomArtGenerateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stores?: ReadonlyArray<StoreOption>;
  areas?: ReadonlyArray<AreaOption>;
}

const CATEGORY_OPTIONS: ReadonlyArray<{
  value: CustomArtCategory;
  label: string;
}> = (Object.keys(CATEGORY_LABELS) as CustomArtCategory[]).map((value) => ({
  value,
  label: CATEGORY_LABELS[value],
}));

export function CustomArtGenerateModal({
  open,
  onOpenChange,
  stores = [],
  areas = [],
}: CustomArtGenerateModalProps) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState<CustomArtCategory>("promo");
  const [prompt, setPrompt] = useState("");
  const [storeId, setStoreId] = useState("");
  const [areaId, setAreaId] = useState("");
  // ID retornado pelo POST /generate; null = ainda não disparou geração.
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const generateMutation = useGenerateCustomArt();
  const queryClient = useQueryClient();
  // Polling automático enquanto generatingId não é null.
  const statusQuery = useCustomArtStatus(generatingId);

  // Reset ao fechar — não queremos polling rodando depois do close.
  useEffect(() => {
    if (!open) {
      setName("");
      setCategory("promo");
      setPrompt("");
      setStoreId("");
      setAreaId("");
      setGeneratingId(null);
      setSubmitError(null);
      generateMutation.reset();
    }
  }, [open, generateMutation]);

  async function handleSubmit() {
    setSubmitError(null);
    if (!name.trim()) {
      setSubmitError("Informe um nome para a arte.");
      return;
    }
    const input: GenerateCustomArtInput = {
      name: name.trim(),
      category,
      prompt: prompt.trim() || undefined,
      store_id: storeId || undefined,
      area_id: areaId || undefined,
    };
    try {
      const res = await generateMutation.mutateAsync(input);
      setGeneratingId(res.art.id);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Falha ao gerar arte.");
    }
  }

  function handleRetry() {
    // Repete a chamada com os mesmos parâmetros. Backend cria uma nova
    // row em 'pending' — a anterior (em 'failed') fica como histórico.
    setGeneratingId(null);
    void handleSubmit();
  }

  function handleClose() {
    if (generatingId && statusQuery.data?.generation_status === "ready") {
      // Garante que listagem reflita a nova arte (já invalida no mutate,
      // mas o useEffect de reset corre antes do invalidate em alguns
      // browsers — força aqui também).
      void queryClient.invalidateQueries({ queryKey: ["custom-arts"] });
    }
    onOpenChange(false);
  }

  const art = statusQuery.data;
  const genStatus = art?.generation_status;
  const isPending = genStatus === "pending" || genStatus === "generating";
  const isReady = genStatus === "ready";
  const isFailed = genStatus === "failed";
  const isGenerating = generateMutation.isPending || isPending;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <div className="flex items-start justify-between gap-2">
          <div>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              <Sparkles
                className="h-4 w-4 text-[var(--color-primary)]"
                aria-hidden="true"
              />
              Gerar arte via IA
            </DialogTitle>
            <DialogDescription className="text-xs text-[var(--color-muted-foreground)]">
              A IA cria o fundo. Você revisa e aprova antes de exibir nas TVs.
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
            aria-label="Fechar"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {generatingId === null ? (
          // ---------------------------------------------------------------
          // Estado 1: formulário pré-geração.
          // ---------------------------------------------------------------
          <div className="mt-4 grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="ai-name">Nome da arte</Label>
              <Input
                id="ai-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex.: Banner picanha promo fim de semana"
                maxLength={200}
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="ai-category">Categoria do fundo</Label>
              <Select
                id="ai-category"
                value={category}
                onChange={(e) =>
                  setCategory(e.target.value as CustomArtCategory)
                }
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Base do prompt — define o estilo visual (cores, atmosfera).
              </p>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="ai-prompt">
                Detalhes (opcional)
              </Label>
              <textarea
                id="ai-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ex.: tons quentes, churrasco em destaque, sem texto na imagem"
                maxLength={2000}
                rows={3}
                className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Texto livre — anexado ao prompt base. Não inclua preço (a TV
                sobrepõe o preço depois).
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="ai-store">Loja (opcional)</Label>
                <Select
                  id="ai-store"
                  value={storeId}
                  onChange={(e) => setStoreId(e.target.value)}
                >
                  <option value="">Todas</option>
                  {stores.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="ai-area">Área (opcional)</Label>
                <Select
                  id="ai-area"
                  value={areaId}
                  onChange={(e) => setAreaId(e.target.value)}
                >
                  <option value="">Nenhuma</option>
                  {areas.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {submitError && (
              <div
                role="alert"
                className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]"
              >
                {submitError}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={handleClose}
                disabled={generateMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => void handleSubmit()}
                disabled={generateMutation.isPending || !name.trim()}
              >
                {generateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                )}
                Gerar arte
              </Button>
            </div>
          </div>
        ) : (
          // ---------------------------------------------------------------
          // Estado 2: pós-geração (pending / generating / ready / failed).
          // ---------------------------------------------------------------
          <div className="mt-4 grid gap-3">
            {isGenerating && (
              <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-[var(--color-border)] p-8 text-center">
                <Loader2
                  className="h-8 w-8 animate-spin text-[var(--color-primary)]"
                  aria-hidden="true"
                />
                <p className="text-sm font-medium">Gerando arte…</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  Isso costuma levar entre 10 e 30 segundos. Pode fechar a janela
                  — a arte vai aparecer na listagem em "Minhas".
                </p>
              </div>
            )}

            {isReady && art && (
              <div className="grid gap-3">
                <div className="overflow-hidden rounded-md border border-[var(--color-border)]">
                  <img
                    src={art.art_url}
                    alt={art.name}
                    className="aspect-video w-full object-cover"
                  />
                </div>
                <div className="text-xs text-[var(--color-muted-foreground)]">
                  Pronto. Custo:{" "}
                  <span className="font-mono tabular-nums text-[var(--color-foreground)]">
                    R$ {art.ai_cost_brl ?? "0,00"}
                  </span>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={handleRetry}>
                    Refazer
                  </Button>
                  <Button onClick={handleClose}>Concluído</Button>
                </div>
              </div>
            )}

            {isFailed && art && (
              <div className="grid gap-3">
                <div
                  role="alert"
                  className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-3 text-xs text-[var(--color-destructive)]"
                >
                  <div className="font-medium">Falha ao gerar a arte.</div>
                  <div className="mt-1 break-words">
                    {art.ai_error_message ?? "Erro desconhecido."}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={handleClose}>
                    Fechar
                  </Button>
                  <Button onClick={handleRetry}>Tentar novamente</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
